/**
 * Monitoring feature type definitions
 * Types for monitors, runs, and related operations
 */

/**
 * Monitor status
 */
export type MonitorStatus = 'enabled' | 'disabled' | 'error';

/**
 * Search behavior when monitor finds new items
 */
export type SearchBehavior = 'append' | 'override';

/**
 * Monitor run status
 */
export type MonitorRunStatus = 'pending' | 'running' | 'completed' | 'failed' | 'canceled';

/**
 * Schedule frequency for UI display
 */
export type ScheduleFrequency = 'daily' | 'weekly' | 'monthly';

/**
 * Day of week for weekly schedules
 */
export type DayOfWeek = 'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat';

/**
 * Monitor response from API
 */
export interface Monitor {
  id: string;
  name: string;
  description?: string;
  status: MonitorStatus;
  lead_table_id?: string;
  lead_table_name?: string;
  provider_monitor_id?: string;
  provider_webset_id?: string;
  cron_expression: string;
  timezone: string;
  max_results: number;
  entity_type: string;
  search_behavior: SearchBehavior;
  search_query?: string;
  criteria?: string[];
  last_run_at?: string;
  next_run_at?: string;
  last_error?: string;
  created_at: string;
  updated_at?: string;
}

/**
 * Monitor run response from API
 */
export interface MonitorRun {
  id: string;
  monitor_id: string;
  provider_run_id?: string;
  status: MonitorRunStatus;
  items_found: number;
  items_added: number;
  error_message?: string;
  started_at: string;
  completed_at?: string;
}

/**
 * Request to create a new monitor
 */
export interface MonitorCreateRequest {
  lead_table_id: string;
  name: string;
  description?: string;
  cron_expression: string;
  timezone?: string;
  max_results?: number;
  search_behavior?: SearchBehavior;
}

/**
 * Request to update an existing monitor
 */
export interface MonitorUpdateRequest {
  name?: string;
  description?: string;
  status?: 'enabled' | 'disabled';
  cron_expression?: string;
  timezone?: string;
  max_results?: number;
  search_behavior?: SearchBehavior;
}

/**
 * Response for listing monitors
 */
export interface MonitorListResponse {
  monitors: Monitor[];
  count: number;
}

/**
 * Response for listing monitor runs
 */
export interface MonitorRunListResponse {
  runs: MonitorRun[];
  count: number;
}

/**
 * Helper type for schedule configuration in wizard
 */
export interface ScheduleConfig {
  frequency: ScheduleFrequency;
  hourOfDay: number;
  daysOfWeek: DayOfWeek[];
  timezone: string;
}

/**
 * Convert schedule config to cron expression
 */
export function scheduleToCron(schedule: ScheduleConfig): string {
  const hour = schedule.hourOfDay;

  switch (schedule.frequency) {
    case 'daily':
      return `0 ${hour} * * *`;
    case 'weekly': {
      // Convert day names to cron day numbers (0 = Sunday)
      const dayMap: Record<DayOfWeek, number> = {
        sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6
      };
      const days = schedule.daysOfWeek.map(d => dayMap[d]).sort().join(',');
      return `0 ${hour} * * ${days || '1'}`;
    }
    case 'monthly':
      return `0 ${hour} 1 * *`;
    default:
      return `0 ${hour} * * *`;
  }
}

/**
 * Parse cron expression to schedule config
 */
export function cronToSchedule(cron: string, timezone: string = 'Etc/UTC'): ScheduleConfig {
  const parts = cron.split(' ');
  const parsedHour = parseInt(parts[1], 10);
  const hour = isNaN(parsedHour) ? 8 : parsedHour;
  const dayOfMonth = parts[2];
  const dayOfWeek = parts[4];

  // Monthly: day of month is specific
  if (dayOfMonth !== '*') {
    return { frequency: 'monthly', hourOfDay: hour, daysOfWeek: [], timezone };
  }

  // Weekly: day of week is specific
  if (dayOfWeek !== '*') {
    const dayMap: Record<string, DayOfWeek> = {
      '0': 'sun', '1': 'mon', '2': 'tue', '3': 'wed', '4': 'thu', '5': 'fri', '6': 'sat'
    };
    const days = dayOfWeek.split(',').map(d => dayMap[d]).filter(Boolean) as DayOfWeek[];
    return { frequency: 'weekly', hourOfDay: hour, daysOfWeek: days, timezone };
  }

  // Default to daily
  return { frequency: 'daily', hourOfDay: hour, daysOfWeek: [], timezone };
}

/**
 * Format schedule for display
 */
export function formatSchedule(monitor: Monitor): string {
  const schedule = cronToSchedule(monitor.cron_expression, monitor.timezone);
  const hour12 = schedule.hourOfDay === 0 ? 12 : schedule.hourOfDay > 12 ? schedule.hourOfDay - 12 : schedule.hourOfDay;
  const period = schedule.hourOfDay < 12 ? 'AM' : 'PM';
  const time = `${hour12}:00 ${period}`;

  switch (schedule.frequency) {
    case 'daily':
      return `Daily at ${time}`;
    case 'weekly': {
      const dayLabels = schedule.daysOfWeek.map(d =>
        d.charAt(0).toUpperCase() + d.slice(1)
      ).join(', ');
      return `Weekly on ${dayLabels || 'Mon'} at ${time}`;
    }
    case 'monthly':
      return `Monthly on the 1st at ${time}`;
    default:
      return `At ${time}`;
  }
}

// ============================================================================
// Create Monitor with Dataset Types (from scratch flow)
// ============================================================================

/**
 * Category of signals/events to monitor
 */
export type EventCategory = 'people' | 'companies' | 'news' | 'custom';

/**
 * Specific event/signal types to monitor
 */
export type EventType =
  // Custom
  | 'custom_event'
  // People signals
  | 'job_change'
  | 'promotion'
  | 'location_change'
  | 'social_media_post'
  // Company signals
  | 'new_hire'
  | 'funding_round'
  | 'product_launch'
  | 'partnership'
  | 'layoff'
  | 'acquisition'
  // News signals
  | 'new_research_publication'
  | 'movie_trailer'
  | 'game_announcement'
  | 'concert'
  | 'music_release';

/**
 * Entity type for result filtering
 * Note: 'person' matches the Exa API entity type
 */
export type ResultEntityType = 'person' | 'company' | 'custom';

/**
 * Configuration for a single signal/event option
 */
export interface EventTypeOption {
  type: EventType;
  label: string;
  icon: string;
  category: EventCategory;
  defaultCriterion: string;
  defaultQuery: string;
}

/**
 * Request to create a monitor with a new dataset (from scratch)
 */
export interface MonitorWithDatasetRequest {
  event_type: EventType;
  custom_event_text?: string;
  entity_type: ResultEntityType;
  custom_entity_description?: string;
  criteria: string[];
  cron_expression: string;
  timezone: string;
  max_results: number;
  search_behavior?: SearchBehavior;
  name?: string;
  description?: string;
}

/**
 * Response from create-monitor-with-dataset endpoint
 */
export interface MonitorWithDatasetResponse {
  monitor: Monitor;
  dataset: {
    id: string;
    name: string;
    webset_id: string;
    status: string;
  };
  message: string;
}
